import { useQuery } from '@tanstack/react-query';
import { getStreamToken } from '@api/services/cctv-camera.service';
import { toast } from 'react-toastify';

const TOKEN_REFRESH_INTERVAL = 30 * 60 * 1000; // 30 minutes

const replaceTokenInUrl = (url: string, newToken: string): string => {
    try {
        const urlObj = new URL(url);
        urlObj.searchParams.set('token', newToken);
        return urlObj.toString();
    } catch {
        return url;
    }
};

interface StreamUrls {
    hlsUrl?: string;
    webrtcUrl?: string;
}

interface UseStreamTokenRefreshReturn extends StreamUrls {
    isLoading: boolean;
    isError: boolean;
    refetch: () => Promise<void>;
}

export const useStreamTokenRefresh = (
    clientId: string | undefined,
    projectId: string | undefined,
    cameraId: string | undefined,
    hlsUrl: string | undefined,
    webrtcUrl: string | undefined
): UseStreamTokenRefreshReturn => {
    const { data, isLoading, isError, refetch: queryRefetch } = useQuery({
        queryKey: ['streamToken', clientId, projectId, cameraId],
        queryFn: async () => {
            const token = await getStreamToken(clientId!, projectId!, cameraId!);
            return {
                hlsUrl: hlsUrl ? replaceTokenInUrl(hlsUrl, token) : undefined,
                webrtcUrl: webrtcUrl ? replaceTokenInUrl(webrtcUrl, token) : undefined,
            };
        },
        enabled: !!(clientId && projectId && cameraId && (hlsUrl || webrtcUrl)),
        refetchInterval: TOKEN_REFRESH_INTERVAL,
        retry: 3,
        onError: () => {
            toast.error('Failed to refresh stream token. Stream may be interrupted.');
        },
    });

    const handleRefetch = async () => {
        try {
            await queryRefetch();
        } catch (error) {
            console.error('Manual token refresh failed:', error);
            toast.error('Failed to refresh stream token.');
        }
    };

    return {
        hlsUrl: data?.hlsUrl,
        webrtcUrl: data?.webrtcUrl,
        isLoading,
        isError,
        refetch: handleRefetch,
    };
};
